/**
 * Wells Fargo PDP financing — SDK init, tagline, and apply card.
 * All credentials and URLs come from Theme settings (data-* on <wells-fargo-financing>).
 */
(function () {
  var scriptPromise = null;

  function ensureWellsFargoScript(src) {
    if (window.WF_SDK_BOOTSTRAP) return Promise.resolve();
    if (!scriptPromise) {
      scriptPromise = new Promise(function (resolve, reject) {
        var existing = document.querySelector('script[data-wells-fargo-sdk="1"]');
        if (existing) {
          if (window.WF_SDK_BOOTSTRAP) {
            resolve();
            return;
          }
          existing.addEventListener('load', function () {
            resolve();
          }, {once: true});
          existing.addEventListener(
            'error',
            function () {
              reject(new Error('Wells Fargo SDK failed'));
            },
            {once: true}
          );
          return;
        }
        var s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.dataset.wellsFargoSdk = '1';
        s.onload = function () {
          resolve();
        };
        s.onerror = function () {
          reject(new Error('Wells Fargo SDK script failed to load'));
        };
        document.body.appendChild(s);
      });
    }
    return scriptPromise;
  }

  function readData(el, name) {
    if (!el) return '';
    var value = el.getAttribute('data-' + name);
    return value == null ? '' : String(value).trim();
  }

  function parseConfig(el) {
    return {
      sdkScriptUrl: readData(el, 'sdk-script-url'),
      gatewayEntityId: readData(el, 'gateway-entity-id'),
      merchantNumber: readData(el, 'merchant-number'),
      environment: readData(el, 'environment') || 'VALIDATION',
      sessionTokenUrl:
        readData(el, 'session-token-url') || readData(el, 'secure-config-url-prefix'),
      sessionTokenUseragent: readData(el, 'session-token-useragent') || 'Mozilla',
      customerSupportPhone: readData(el, 'customer-support-phone') || '18884023475',
    };
  }

  function isConfigComplete(config) {
    return !!(
      config &&
      config.sdkScriptUrl &&
      config.gatewayEntityId &&
      config.merchantNumber &&
      config.sessionTokenUrl
    );
  }

  function buildSecureConfigurationUrl(sessionTokenUrl, ip, useragent) {
    if (!sessionTokenUrl || !ip) return '';

    var ua = useragent || 'Mozilla';

    if (sessionTokenUrl.indexOf('session-token') >= 0) {
      var base = sessionTokenUrl.split('?')[0].replace(/\/$/, '');
      return (
        base +
        '?ipaddress=' +
        encodeURIComponent(ip) +
        '&useragent=' +
        encodeURIComponent(ua)
      );
    }

    var legacyPrefix = sessionTokenUrl.replace(/\/$/, '') + '/';
    return legacyPrefix + ip + '/useragent/' + (ua || 'browseruser');
  }

  function waitForWellsFargoPayment(maxAttempts) {
    var attempts = maxAttempts || 50;
    return new Promise(function (resolve, reject) {
      function check() {
        if (window.WellsFargoPayment) {
          resolve(window.WellsFargoPayment);
          return;
        }
        attempts -= 1;
        if (attempts <= 0) {
          reject(new Error('WellsFargoPayment helper did not load'));
          return;
        }
        window.setTimeout(check, 20);
      }
      check();
    });
  }

  function ensureSdkInitialized(config, instance) {
    if (window.sdkHandler) return Promise.resolve(window.sdkHandler);
    if (!ensureSdkInitialized.promise) {
      ensureSdkInitialized.promise = (async function () {
        var ipRes = await fetch('https://api.ipify.org/?format=json');
        var ipJson = await ipRes.json();
        var newIp = ipJson && ipJson.ip;
        if (!newIp) {
          throw new Error('Could not resolve client IP');
        }

        var secureConfigurationUrl = buildSecureConfigurationUrl(
          config.sessionTokenUrl,
          newIp,
          config.sessionTokenUseragent
        );
        if (!secureConfigurationUrl) {
          throw new Error('Could not build secureConfigurationUrl');
        }

        window.WF_AO_SDK_Config = {
          gatewayEntityId: config.gatewayEntityId,
          silentInit: true,
          secureConfigurationUrl: secureConfigurationUrl,
          targetDivId: 'wells_card_offer',
          displayPrequalification: false,
          displayApplication: true,
          merchantNumber: config.merchantNumber,
          customerSupportPhoneNumber: config.customerSupportPhone,
          environment: config.environment,
          loggingEnabled: false,
          consoleLoggingEnabled: false,
          displayPhoneBasedId: true,
        };

        if (instance) {
          instance.attachPaycalcCallbacks();
        }

        await ensureWellsFargoScript(config.sdkScriptUrl);
        if (window.WF_SDK_BOOTSTRAP && !window.sdkHandler) {
          window.sdkHandler = window.WF_SDK_BOOTSTRAP.initialize();
        }
        return window.sdkHandler;
      })().catch(function (error) {
        ensureSdkInitialized.promise = null;
        throw error;
      });
    }

    return ensureSdkInitialized.promise;
  }

  class WellsFargoFinancing extends HTMLElement {
    constructor() {
      super();
      this.cancelled = false;
      this.defaultPlanId = '';
      this.selectedClientUniqueId = '';
      this.planRoot = null;
      this.calculations = [];
      this.rawTagline = '';
      this.sdkReady = false;
      this.config = null;
      this.variantUnsubscriber = null;
      this.resizeHandler = null;
    }

    connectedCallback() {
      this.taglineEl = this.querySelector('[data-wf-tagline]');
      this.modalEl = this.querySelector('[data-wf-modal]');
      this.overlayEl = this.querySelector('[data-wf-overlay]');
      this.offerEl = this.querySelector('#wells_card_offer');
      this.viewTermsBtns = this.querySelectorAll('[data-wf-view-terms]');
      this.config = parseConfig(this);

      var self = this;
      this.viewTermsBtns.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          self.toggleModal();
        });
      });
      if (this.overlayEl) {
        this.overlayEl.addEventListener('click', function () {
          self.toggleModal(false);
        });
      }

      if (this.overlayEl) document.body.appendChild(this.overlayEl);
      if (this.modalEl) document.body.appendChild(this.modalEl);

      this.resizeHandler = function () {
        self.refreshTaglineLayout();
      };
      window.addEventListener('resize', this.resizeHandler);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
          if (!event || !event.data) return;
          var sectionId = self.getAttribute('data-section-id');
          if (event.data.sectionId && sectionId && event.data.sectionId !== sectionId) return;
          var variant = event.data.variant;
          if (variant && typeof variant.price === 'number') {
            self.updateCalcAmount(variant.price / 100);
          }
        });
      }

      this.bootstrap();
    }

    disconnectedCallback() {
      this.cancelled = true;
      if (this.variantUnsubscriber) this.variantUnsubscriber();
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.overlayEl && this.overlayEl.parentNode) this.overlayEl.parentNode.removeChild(this.overlayEl);
      if (this.modalEl && this.modalEl.parentNode) this.modalEl.parentNode.removeChild(this.modalEl);
      document.body.style.overflow = '';
    }

    get calcAmount() {
      var raw = parseFloat(this.getAttribute('data-calc-amount') || '0', 10);
      return Number.isFinite(raw) ? raw : 0;
    }

    updateCalcAmount(amount) {
      this.setAttribute('data-calc-amount', String(amount));
      this.syncCalculations();
      this.invokePayCalc();
    }

    syncCalculations() {
      if (!this.planRoot || !window.WellsFargoPayment) {
        this.calculations = [];
        this.defaultPlanId = '';
        return null;
      }

      var payload = window.WellsFargoPayment.getWellsFargoPayCalcPayload(
        this.calcAmount,
        this.planRoot
      );
      this.calculations = payload && payload.calculations ? payload.calculations : [];
      this.defaultPlanId = payload && payload.defaultPlanId ? payload.defaultPlanId : '';
      return payload;
    }

    attachPaycalcCallbacks() {
      if (!window.WF_AO_SDK_Config) return;
      var self = this;
      window.WF_AO_SDK_Config.callbacks = {
        general: {
          ready: function () {
            self.invokePayCalc();
          },
        },
        paycalc: {
          success: function (data) {
            self.handlePaycalcSuccess(data);
          },
          failure: function () {
            self.hideWidget();
          },
        },
      };
    }

    hideWidget() {
      this.hidden = true;
    }

    async bootstrap() {
      if (!isConfigComplete(this.config)) {
        this.hideWidget();
        return;
      }

      var planUrl = this.getAttribute('data-payment-plan-url');
      var sdkPromise = this.startSdkInit();
      var planPromise = Promise.resolve();

      if (planUrl) {
        planPromise = (async function () {
          try {
            await waitForWellsFargoPayment();
            var res = await fetch(planUrl);
            if (!res.ok) return;
            this.planRoot = await res.json();
            this.syncCalculations();
          } catch (e) {
            /* payment plan optional for SDK token; widget hides if no calculations */
          }
        }).call(this);
      }

      await Promise.all([sdkPromise, planPromise]);

      if (!this.calculations.length) {
        this.hideWidget();
        return;
      }

      this.hidden = false;
      this.invokePayCalc();
    }

    async startSdkInit() {
      if (this.cancelled) return;
      if (this.sdkReady && window.sdkHandler) return;
      try {
        await ensureSdkInitialized(this.config, this);
        if (this.cancelled) return;
        this.sdkReady = true;
      } catch (e) {
        this.hideWidget();
      }
    }

    invokePayCalc() {
      if (!this.calculations.length) {
        return false;
      }
      this.hidden = false;
      if (!window.sdkHandler) return false;
      try {
        window.sdkHandler.payCalc({calculations: this.calculations});
        return true;
      } catch (e) {
        this.hideWidget();
        return false;
      }
    }

    handlePaycalcSuccess(data) {
      var tagLines = data && data.tagLines;
      var pid = this.defaultPlanId;
      var match = (tagLines || []).filter(function (item) {
        return item.client_unique_id === 'RDJ' + pid;
      });
      var product = match[0];
      if (!product || !product.tagLine) {
        return;
      }
      this.selectedClientUniqueId = product.client_unique_id;
      this.rawTagline = product.tagLine;
      this.setTaglineHtml(product.tagLine);
    }

    setTaglineHtml(rawTagline) {
      if (!this.taglineEl || !window.WellsFargoPayment) return;
      var isDesktop = window.innerWidth > 767;
      this.taglineEl.innerHTML = window.WellsFargoPayment.formatWellsFargoTaglineHtml(
        rawTagline,
        isDesktop
      );
      this.taglineEl.classList.remove('wells-fargo-financing__tagline--placeholder');
      var taglineWrap = this.taglineEl.closest('.wells-fargo-financing__tagline-wrap');
      if (taglineWrap) taglineWrap.classList.remove('wells-fargo-financing__tagline-wrap--empty');
    }

    refreshTaglineLayout() {
      if (!this.rawTagline) return;
      this.setTaglineHtml(this.rawTagline);
    }

    toggleModal(forceOpen) {
      if (!this.modalEl) return;
      var open =
        typeof forceOpen === 'boolean' ? forceOpen : !this.modalEl.classList.contains('is-open');
      if (open && !this.selectedClientUniqueId) {
        return;
      }
      this.modalEl.classList.toggle('is-open', open);
      if (this.overlayEl) this.overlayEl.classList.toggle('is-open', open);
      this.classList.toggle('is-modal-open', open);
      document.body.style.overflow = open ? 'hidden' : '';

      if (open && window.sdkHandler && this.selectedClientUniqueId) {
        window.sdkHandler.display('paycalc', {
          client_unique_id: this.selectedClientUniqueId,
        });
      }
    }
  }

  if (!customElements.get('wells-fargo-financing')) {
    customElements.define('wells-fargo-financing', WellsFargoFinancing);
  }
})();
